import type { CardPrintTemplate, DistrictMode } from '@citizen-walk/shared-types';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * Card-print layout pure helpers (admin 第 21 弾、Phase 5 A-S6b / GAP-A8)。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/constants.ts::DEFAULT_FIELDS_BY_MODE` の
 * ハードコード fields を撤廃し、M7 `cardPrintTemplates` master の `fields` 配列で動的化する
 * ための pure helper 群。
 *
 * 認可・I/O 副作用は一切持たない (test 容易)。
 */

export type CardPrintField = CardPrintTemplate['fields'][number];

/**
 * dot / bracket path で object をたどる素直な実装。
 *
 * サポート:
 *   - 'a.b.c' (ネスト)
 *   - 'a.b[0].c' (配列インデックス、`a.b.0.c` でも同等)
 *   - 不在パスは ''
 *   - 途中 null/undefined のチェーンは '' で早期終了
 *   - 終端が primitive (string / number / boolean) なら String(value)
 *   - 終端が配列なら primitive 要素を `、` で join (object 要素は除外)。
 *     AI 解析の `ecw.emotions` / `ecw.categoryCodes` や extraFields の配列値を
 *     「自由に持ってくる」用途に対応 (old2 `getExtraField` の配列 join 踏襲)。
 *   - それ以外 (object / 関数 / Date 等) は ''
 */
export function getByPath(obj: unknown, path: string): string {
  if (obj == null) return '';
  if (!path) return '';
  // 'a.b[0].c' → ['a', 'b', '0', 'c']
  const segments = path
    .replace(/\[(\w+)\]/g, '.$1')
    .split('.')
    .filter((s) => s.length > 0);
  let cur: unknown = obj;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return '';
    cur = (cur as Record<string, unknown>)[seg];
  }
  return stringifyLeaf(cur);
}

/** primitive / 配列 (primitive 要素のみ) を表示文字列化。それ以外は ''。 */
function stringifyLeaf(cur: unknown): string {
  if (cur == null) return '';
  if (typeof cur === 'string') return cur;
  if (typeof cur === 'number' || typeof cur === 'boolean') return String(cur);
  if (Array.isArray(cur)) {
    return cur
      .filter((v) => v != null && typeof v !== 'object')
      .map((v) => String(v))
      .join('、');
  }
  return '';
}

/**
 * fieldPath を持たない組み込みフィールド (key 駆動の computed extractor) のキー集合。
 *
 * old2 `fieldExtractors.ts` の bespoke extractor (formatCreatedAt / getAiSummary /
 * getAiTopCategory / getAiEmotion / formatLocation) を v2 の構造化 aiAnalysis
 * (ecw / bousai / walker / generic) へ移植したもの。テンプレートに依存せず、
 * 単純な path では取れない (Date 整形 / 配列 join / mode 非依存 / rank 並べ替え) 値を扱う。
 */
export const COMPUTED_FIELD_KEYS = new Set<string>([
  'createdAt',
  'location',
  'aiSummary',
  'aiTopCategory',
  'aiEmotion',
  'sessionName',
]);

/**
 * computed field の値解決に必要な外部マップ (path では取れない参照解決用)。
 *   - sessionNameMap: post.sessionId → セッション名 (左フィルタで読込んだ sessions 一覧から構築)。
 *
 * 全て optional。未指定 (テスト / セッション未読込) の場合、参照解決系 (sessionName) は空文字を返す。
 */
export interface ComputedFieldContext {
  sessionNameMap?: Record<string, string>;
}

function formatCreatedAt(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}`;
}

function formatLocation(loc: CardPrintPost['location']): string {
  if (!loc) return '';
  const { latitude: lat, longitude: lng } = loc;
  if (typeof lat !== 'number' || typeof lng !== 'number') return '';
  if (lat === 0 && lng === 0) return '';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/**
 * 投稿の緯度経度から Google Maps URL を生成 (QR コードの遷移先)。
 *
 * QR は「投稿リンク」ではなく「その場所を地図で開く」ためのもの。
 *
 * 形式は **座標プレイス方式** `https://www.google.com/maps/place/<lat>,<lng>` を使う。
 * 旧 `?q=<lat>,<lng>` (検索方式、old2 `buildMapsUrl` 踏襲) は座標が店舗の近くだと
 * 近隣 POI にスナップし、店舗カードを閉じるとピンも消える問題があった。`/maps/place/`
 * は緯度経度そのものを「場所」として開くため座標ピンが立ち、POI へスナップしにくく
 * ピンが残りやすい。
 *
 * 座標は 6 桁 (≒0.1m 精度) に丸めて URL を短くし、QR を疎に (モジュールを大きく) する
 * — インクジェット印刷のにじみで読み取りにくくなるのを抑えるため。
 *
 * 位置情報が無い / (0,0) は null (= QR を描画しない)。
 */
export function buildPostMapUrl(post: CardPrintPost): string | null {
  const loc = post.location;
  if (!loc) return null;
  const { latitude: lat, longitude: lng } = loc;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (lat === 0 && lng === 0) return null;
  // toFixed(6) → Number で末尾 0 を落とす (35.658600 ではなく 35.6586)。
  const la = Number(lat.toFixed(6));
  const ln = Number(lng.toFixed(6));
  return `https://www.google.com/maps/place/${la},${ln}`;
}

/** mode 非依存の AI 概要 (ecw / bousai / walker / generic の description を順に拾う)。 */
function getAiSummary(ai: CardPrintPost['aiAnalysis']): string {
  if (!ai) return '';
  if (ai.ecw?.description) return ai.ecw.description;
  if (ai.bousai?.description) return ai.bousai.description;
  if (ai.walker?.description) return ai.walker.description;
  const gen = ai.generic;
  if (gen && typeof gen === 'object') {
    const d = (gen as Record<string, unknown>).description;
    if (typeof d === 'string' && d) return d;
  }
  return '';
}

/** AI 主要カテゴリ (bousai/walker は classifications を rank 昇順で先頭、ecw は categoryCodes 先頭)。 */
function getAiTopCategory(ai: CardPrintPost['aiAnalysis']): string {
  if (!ai) return '';
  const cls = ai.bousai?.classifications ?? ai.walker?.classifications;
  if (Array.isArray(cls) && cls.length > 0) {
    const top = [...cls].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))[0]!;
    return (
      top.category_name ||
      (top as { hzr_name?: string }).hzr_name ||
      (top as { gql_name?: string }).gql_name ||
      ''
    );
  }
  if (Array.isArray(ai.ecw?.categoryCodes) && ai.ecw!.categoryCodes.length > 0) {
    return ai.ecw!.categoryCodes[0] ?? '';
  }
  return '';
}

/** AI 感情 (ecw.emotionText 優先、無ければ emotions を `・` で join、generic.emotions も fallback)。 */
function getAiEmotion(ai: CardPrintPost['aiAnalysis']): string {
  if (!ai) return '';
  if (ai.ecw?.emotionText) return ai.ecw.emotionText;
  if (Array.isArray(ai.ecw?.emotions) && ai.ecw!.emotions.length > 0) {
    return ai.ecw!.emotions.join('・');
  }
  const gen = ai.generic as Record<string, unknown> | undefined;
  const ge = gen?.emotions;
  if (Array.isArray(ge) && ge.length > 0) return ge.map((v) => String(v)).join('・');
  return '';
}

/** post.sessionId をセッション名に変換 (未設定 / マップ該当なしは空文字)。 */
function getSessionName(
  post: CardPrintPost,
  sessionNameMap: Record<string, string> | undefined,
): string {
  const id = post.sessionId;
  if (!id || !sessionNameMap) return '';
  return sessionNameMap[id] ?? '';
}

/**
 * fieldPath を持たない組み込みフィールドの値を key から計算する。
 * 未知の key は '' (qrCode / 管理者追記 等は呼び出し側で別途描画するため通常到達しない)。
 *
 * `ctx` は参照解決系 computed field (sessionName) のための外部マップ。未指定なら該当 field は空文字。
 */
export function extractComputedFieldValue(
  post: CardPrintPost,
  key: string,
  ctx?: ComputedFieldContext,
): string {
  switch (key) {
    case 'createdAt':
      return formatCreatedAt(post.createdAt);
    case 'location':
      return formatLocation(post.location);
    case 'aiSummary':
      return getAiSummary(post.aiAnalysis);
    case 'aiTopCategory':
      return getAiTopCategory(post.aiAnalysis);
    case 'aiEmotion':
      return getAiEmotion(post.aiAnalysis);
    case 'sessionName':
      return getSessionName(post, ctx?.sessionNameMap);
    default:
      return '';
  }
}

/**
 * field 1 つを post から抽出する。
 *
 * - `fieldPath` が空 → `key` 駆動の computed extractor (組み込みフィールド)。
 *   テンプレート fields は常に fieldPath を持つため、この分岐は組み込みカタログ専用。
 * - `fieldPath` あり → source 別 root object を path でたどる:
 *   - 'post'        → post (id / title / comment / agreeCount 等)
 *   - 'extraFields' → post.extraFields (地区固有データ)
 *   - 'aiAnalysis'  → post.aiAnalysis (`ecw.emotions` / `bousai.classifications[0].category_name` 等)
 *   - 'author'      → 限定 view ({ authorNickname, authorUid }) を露出 (旧 v1 互換、最低限)
 *
 * `ctx` は computed field (sessionName 等) の参照解決マップ。fieldPath ありの分岐では未使用。
 */
export function extractFieldValue(
  post: CardPrintPost,
  field: CardPrintField,
  ctx?: ComputedFieldContext,
): string {
  if (!field.fieldPath) {
    return extractComputedFieldValue(post, field.key, ctx);
  }
  switch (field.source) {
    case 'post':
      return getByPath(post, field.fieldPath);
    case 'extraFields':
      return getByPath(post.extraFields, field.fieldPath);
    case 'aiAnalysis':
      return getByPath(post.aiAnalysis, field.fieldPath);
    case 'author':
      return getByPath(
        { authorNickname: post.authorNickname, authorUid: post.authorUid },
        field.fieldPath,
      );
    default:
      // 型上ありえないが defensive (新 source 追加時の guard)
      return '';
  }
}

/**
 * fields を `displayOrder` 昇順で sort (immutable copy)。
 *
 * displayOrder が同値なら入力配列順を保つ (Array.prototype.sort は modern JS では stable)。
 */
export function sortFields<T extends { displayOrder: number }>(fields: readonly T[]): T[] {
  return [...fields].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * 引数 mode に一致する非 archived の最初の template を返す。
 *
 * 選択基準:
 *   1. `archivedAt === null`
 *   2. `mode === target`
 *   3. 最初の 1 件 (master ページの displayName 昇順を踏襲)
 *
 * 該当なしは null。0 件 / mode 不一致時の UI fallback は呼び出し側 (CardPrintPage) で表示。
 */
export function pickActiveTemplate<T extends { mode: DistrictMode; archivedAt: string | null }>(
  templates: readonly T[],
  mode: DistrictMode,
): T | null {
  for (const t of templates) {
    if (t.archivedAt === null && t.mode === mode) return t;
  }
  return null;
}
